import { PRODUCTS, isProductKey, type OrgRole, type TraceEnvironment } from '@traceio-org/platform-contracts';
import type { RecordStatusTone } from '@traceio-org/ui';

// One wording for staff roles everywhere: the contracts own it.
export { STAFF_ROLE_LABELS } from '@traceio-org/platform-contracts';

/** Up to two initials from a display name (Taylor Morgan: TM). */
export function initialsOf(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((word) => Array.from(word)[0] ?? '')
    .join('')
    .toUpperCase();
}

/** A product's name as the brand writes it; an unknown key is shown as it came. */
export function productName(productKey: string): string {
  return isProductKey(productKey) ? PRODUCTS[productKey].displayName : productKey;
}

/** The short product name for dense lists (Brokers, Customer Portal). */
export function productShortName(productKey: string): string {
  return isProductKey(productKey) ? PRODUCTS[productKey].shortName : productKey;
}

export interface StatusPresentation {
  label: string;
  tone: RecordStatusTone;
}

/**
 * Status words platform-api sends, in plain English with a tone. The contract types several of them as
 * free strings, so an unknown value is shown as it came, in a neutral tone, never hidden or guessed.
 */
const STATUSES: Readonly<Record<string, StatusPresentation>> = {
  // Organizations
  active: { label: 'Active', tone: 'positive' },
  suspended: { label: 'Suspended', tone: 'danger' },
  // Product subscriptions
  trialing: { label: 'Trial', tone: 'info' },
  past_due: { label: 'Past due', tone: 'warning' },
  canceled: { label: 'Canceled', tone: 'neutral' },
  // Domain verification, TLS and lifecycle
  pending: { label: 'Pending', tone: 'warning' },
  verified: { label: 'Verified', tone: 'positive' },
  failed: { label: 'Failed', tone: 'danger' },
  unknown: { label: 'Unknown', tone: 'neutral' },
  ready: { label: 'Ready', tone: 'positive' },
  disabled: { label: 'Disabled', tone: 'warning' },
  deprovisioning: { label: 'Deprovisioning', tone: 'warning' },
  released: { label: 'Released', tone: 'neutral' },
  // Memberships
  invited: { label: 'Invited', tone: 'info' },
  removed: { label: 'Removed', tone: 'neutral' },
  // Entitlements
  scheduled: { label: 'Scheduled', tone: 'info' },
  ended: { label: 'Ended', tone: 'neutral' },
  // Invitations
  accepted: { label: 'Accepted', tone: 'positive' },
  expired: { label: 'Expired', tone: 'neutral' },
  revoked: { label: 'Revoked', tone: 'neutral' },
  // Billing: plan versions, periods, adjustments, documents (contracts reuse scheduled, trialing, active, ended)
  draft: { label: 'Draft', tone: 'warning' },
  published: { label: 'Published', tone: 'positive' },
  open: { label: 'Open', tone: 'info' },
  closed: { label: 'Closed', tone: 'neutral' },
  approved: { label: 'Approved', tone: 'positive' },
  rejected: { label: 'Rejected', tone: 'danger' },
  paid: { label: 'Paid', tone: 'positive' },
  void: { label: 'Void', tone: 'neutral' },
  uncollectible: { label: 'Uncollectible', tone: 'danger' },
  // Internal organizations: billing exempt (spec 2026-10-18 piece 1).
  billing_exempt: { label: 'Not billed', tone: 'info' },
  // Ticket labels
  archived: { label: 'Archived', tone: 'neutral' },
};

export function presentStatus(status: string): StatusPresentation {
  return STATUSES[status] ?? { label: status, tone: 'neutral' };
}

export const DOMAIN_KIND_LABELS: Readonly<Record<string, string>> = {
  'employee-app': 'Employee app',
  'customer-portal': 'Customer portal',
};

export const SSO_PROVIDER_LABELS: Readonly<Record<string, string>> = {
  microsoft: 'Microsoft',
  google: 'Google',
  saml: 'SAML',
  oidc: 'OpenID Connect',
};

// Records are dated in UTC: every staff member, wherever they are, reads the same date for the same row.
const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' });
const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'UTC',
});

/** A calendar date in the viewer's locale, in UTC (Oct 3, 2026). */
export function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : dateFormat.format(date);
}

/** Date and time in UTC, for the title attribute next to a date. */
export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : `${dateTimeFormat.format(date)} UTC`;
}

const countFormat = new Intl.NumberFormat();

export function formatCount(value: number): string {
  return countFormat.format(value);
}

export function plural(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

const timeFormat = new Intl.DateTimeFormat(undefined, { timeStyle: 'short', timeZone: 'UTC' });

/** The time of day in UTC (3:42 PM UTC), for a moment the same day. */
export function formatTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : `${timeFormat.format(date)} UTC`;
}

function hoursAndMinutes(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(plural(hours, 'hour', 'hours'));
  if (minutes > 0 || hours === 0) parts.push(plural(minutes, 'minute', 'minutes'));
  return parts.join(' ');
}

/** A duration in whole minutes, in words (90: 1 hour 30 minutes). */
export function formatDuration(minutes: number): string {
  return hoursAndMinutes(Math.max(0, Math.round(minutes)));
}

/** Time left until a moment, in words, rounded down to the minute (38 minutes, less than a minute). */
export function formatRemaining(milliseconds: number): string {
  if (milliseconds <= 0) return 'no time';
  if (milliseconds < 60_000) return 'less than a minute';
  return hoursAndMinutes(Math.floor(milliseconds / 60_000));
}

export const ORG_ROLE_LABELS: Readonly<Record<OrgRole, string>> = {
  org_owner: 'Owner',
  org_admin: 'Administrator',
  billing_admin: 'Billing administrator',
  member: 'Member',
  help_desk: 'Help desk',
};

export const ENTITLEMENT_SOURCE_LABELS: Readonly<Record<string, string>> = {
  plan: 'Plan',
  contract: 'Contract',
  trial: 'Trial',
  support_grant: 'Support grant',
};

export const RELEASE_STATUS_LABELS: Readonly<Record<string, string>> = {
  ga: 'Generally available',
  beta: 'Beta',
  alpha: 'Alpha',
  internal: 'Internal only',
};

export const ENVIRONMENT_LABELS: Readonly<Record<TraceEnvironment, string>> = {
  development: 'Development',
  test: 'Test',
  staging: 'Staging',
  production: 'Production',
};

export type Rollout =
  { kind: 'all' } | { kind: 'organizations'; organizationIds: readonly string[] } | { kind: 'off' };

/** A release flag's rollout in words. */
export function describeRollout(rollout: Rollout): string {
  switch (rollout.kind) {
    case 'all':
      return 'Every organization';
    case 'off':
      return 'Off';
    case 'organizations':
      return plural(rollout.organizationIds.length, 'organization', 'organizations');
  }
}

/** Longest audit value shown in full; longer ones are cut, the rest is in the log itself. */
const MAX_AUDIT_VALUE_LENGTH = 240;

/**
 * An audit change value (any JSON the server recorded, already redacted) as plain text. It is only ever
 * rendered as text, so markup in a value shows as characters and never runs.
 */
export function formatAuditValue(value: unknown): string {
  let text: string;
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') text = value;
  else if (typeof value === 'number' || typeof value === 'bigint') text = String(value);
  else {
    try {
      text = JSON.stringify(value) ?? String(value);
    } catch {
      text = String(value);
    }
  }
  return text.length > MAX_AUDIT_VALUE_LENGTH ? `${text.slice(0, MAX_AUDIT_VALUE_LENGTH)}\u2026` : text;
}

/** A calendar day (YYYY-MM-DD) in UTC, the zone every Trace Admin date is shown in. */
export function utcDay(time: number): string {
  return new Date(time).toISOString().slice(0, 10);
}

/** The UTC day `days` after a YYYY-MM-DD day. */
export function addDays(day: string, days: number): string {
  return utcDay(Date.parse(`${day}T00:00:00.000Z`) + days * 86_400_000);
}

/** True for a real YYYY-MM-DD calendar day. */
export function isDay(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && utcDay(Date.parse(`${value}T00:00:00.000Z`) || 0) === value;
}

/** The first and the last instant of a UTC day, as platform-api takes date-times. */
export function startOfDay(day: string): string {
  return `${day}T00:00:00.000Z`;
}

export function endOfDay(day: string): string {
  return `${day}T23:59:59.999Z`;
}

/* Billing (milestone 3b). Units come from the contract: money in micros, tokens in millitokens. */

export const PLAN_KIND_LABELS: Readonly<Record<string, string>> = {
  catalog: 'Catalog',
  custom: 'Custom',
};

export const PRICE_COMPONENT_KIND_LABELS: Readonly<Record<string, string>> = {
  flat_recurring: 'Fixed amount per period',
  licensed_quantity: 'Per purchased unit',
  metered_curve: 'Metered tokens on a falling curve',
  metered_graduated: 'Metered tokens in price bands',
  conditional_discount: 'Conditional discount',
};

export const BILLABLE_ACTION_LABELS: Readonly<Record<string, string>> = {
  quoting: 'Quoting',
  negotiation: 'Negotiation',
  booking: 'Booking',
  tracking: 'Tracking',
  email_intake: 'Email intake',
  lane_check: 'Lane check',
  text_message: 'Text message',
  call_minute: 'Call minute',
  assistant_reply: 'Assistant reply',
};

export const LINE_ITEM_KIND_LABELS: Readonly<Record<string, string>> = {
  base: 'Base',
  licensed_quantity: 'Purchased units',
  tokens: 'Tokens',
  discount: 'Discount',
  adjustment: 'Adjustment',
  trial_credit: 'Trial credit',
};

export const ADJUSTMENT_KIND_LABELS: Readonly<Record<string, string>> = {
  credit: 'Credit',
  debit: 'Debit',
};

export const DISCOUNT_PRORATION_LABELS: Readonly<Record<string, string>> = {
  whole_period: 'Whole period: only when the condition held at the end of every day',
  by_day: 'By day: the share of days at whose end the condition held',
};

export const BILLING_PROVIDER_LABELS: Readonly<Record<string, string>> = {
  fake: 'Local fake provider',
  stripe: 'Stripe',
};

export const LICENSED_UNIT_LABELS: Readonly<Record<string, string>> = {
  carrier_connection: 'carrier connection',
};

/** The label of a billable action, or the key as it came. */
export function actionLabel(action: string): string {
  return BILLABLE_ACTION_LABELS[action] ?? action;
}

/** A plan version's number as the screens name it (v3). */
export function versionLabel(version: number): string {
  return `v${version}`;
}

/** A billing period in words, from its start to its end (both UTC dates). */
export function formatPeriod(period: { start: string; end: string }): string {
  return `${formatDate(period.start)} to ${formatDate(period.end)}`;
}
